import { isAxiosError } from 'axios';
import { ChangeEvent, useState } from 'react';
import { toast } from 'sonner';
import { reportsApi } from '@/api/reports';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { ReportData } from '@/types/report';

interface SaveTemplateModalProps {
    onNext: () => void;
    data: ReportData;
}

export function SaveTemplateModal({ onNext, data }: SaveTemplateModalProps) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const saveTemplateAccess = useFeatureEntitlement('reportTemplates.custom');

    const handleSave = async () => {
        if (saveTemplateAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(saveTemplateAccess.decision));
            return;
        }

        if (!name.trim()) {
            toast.error('Por favor, dê um nome ao modelo.');
            return;
        }

        setIsSubmitting(true);

        try {
            const {
                id,
                patient,
                anamnesis,
                status,
                created_at,
                updated_at,
                tests_performed_at_start,
                tests_performed_at_end,
                has_anamnesis,
                customizations,
                ...rest
            } = data;

            // Clean customizations
            const cleanCustomizations = { ...customizations };
            if (cleanCustomizations.template) {
                delete cleanCustomizations.template;
            }

            const sanitizedTests = rest.tests
                ? {
                      ...rest.tests,
                      blocks: rest.tests.blocks
                          .map((block) => {
                              if (block.block_type === 'chart') {
                                  return {
                                      ...block,
                                      automatic: false,
                                      datapoints: {
                                          x_axis: [],
                                          y_axis: [],
                                      },
                                      has_content: false,
                                  };
                              }

                              return {
                                  ...block,
                                  automatic: false,
                              };
                          })
                          .filter(Boolean),
                  }
                : undefined;

            const templatePayload = {
                ...rest,
                name,
                description,
                customizations: cleanCustomizations,
            };

            if (sanitizedTests?.blocks?.length) {
                templatePayload.tests = sanitizedTests;
            }

            await reportsApi.saveTemplate(templatePayload);
            toast.success('Modelo salvo com sucesso!');
            onNext();
        } catch (error) {
            console.error('Failed to save template', error);

            if (isAxiosError(error) && error.response?.status === 409) {
                toast.error('Já existe um modelo com este nome.');
            } else {
                toast.error('Erro ao salvar o modelo.');
            }

            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="space-y-4">
                <div>
                    <label htmlFor="template-name" className="text-sm font-medium text-foreground">
                        Nome do Modelo <span className="text-red-500">*</span>
                    </label>
                    <Input
                        id="template-name"
                        value={name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                        placeholder="Ex: Avaliação TDAH Adulto"
                    />
                </div>

                <div className="space-y-2">
                    <label
                        htmlFor="template-description"
                        className="text-sm font-medium text-foreground"
                    >
                        Descrição
                    </label>
                    <Textarea
                        id="template-description"
                        value={description}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                            setDescription(e.target.value)
                        }
                        placeholder="Descreva brevemente o propósito deste modelo"
                        rows={3}
                    />
                </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-border pt-4">
                <Button
                    variant="ghost"
                    onClick={onNext}
                    disabled={isSubmitting}
                    className="text-muted-foreground hover:text-foreground"
                >
                    Agora não
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={!name.trim() || isSubmitting}
                    aria-disabled={saveTemplateAccess.shouldBlock}
                    className="bg-[#00006B] text-white hover:bg-[#00006B]/90"
                >
                    {isSubmitting ? 'Salvando...' : 'Salvar como Modelo'}
                    {saveTemplateAccess.isUnavailableInPlan &&
                    saveTemplateAccess.availableFromPlan ? (
                        <FeatureUnavailableChip
                            availablePlan={saveTemplateAccess.availableFromPlan}
                        />
                    ) : null}
                </Button>
            </div>
        </div>
    );
}
